
import React, { useState } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import InsightsHeader from '@/components/insights/InsightsHeader';
import FeaturedInsightsSection from '@/components/insights/FeaturedInsightsSection';
import InsightsFilters from '@/components/insights/InsightsFilters';
import InsightsList from '@/components/insights/InsightsList';
import { useFilteredInsights } from '@/hooks/useFilteredInsights';

const InsightsPage = () => {
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  // Use the existing hook to fetch insights data
  const { insights, isLoading, error } = useFilteredInsights(categoryFilter, null, 20);

  // Handle category filter change
  const handleCategoryChange = (category: string | null) => {
    setCategoryFilter(category);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle search query change
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Calculate pagination
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  
  // Filter insights based on search query
  const filteredInsights = insights.filter(insight =>
    insight.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    insight.content.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Get current page insights
  const currentInsights = filteredInsights.slice(startIndex, endIndex);
  
  // Calculate total pages
  const totalPages = Math.ceil(filteredInsights.length / pageSize);

  return (
    <MainLayout>
      <div className="container max-w-screen-xl mx-auto px-4 py-6">
        <InsightsHeader />
        
        {/* Featured Insights Section */}
        <section className="mb-12">
          <FeaturedInsightsSection />
        </section>
        
        {/* Browse All Insights Section */}
        <section>
          <div className="mb-6">
            <h2 className="text-2xl font-bold">Browse All Insights</h2>
            <p className="text-gray-500 dark:text-gray-400">
              Explore all insights and filter by category or search by keyword
            </p>
          </div>
          
          {/* Filters */}
          <InsightsFilters 
            onCategoryChange={handleCategoryChange}
            onSearchChange={handleSearchChange}
            selectedCategory={categoryFilter}
            searchQuery={searchQuery}
          />
          
          {/* Insights Grid */}
          <InsightsList 
            insights={currentInsights}
            isLoading={isLoading}
            error={error}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </section>
      </div>
    </MainLayout>
  );
};

export default InsightsPage;
